import * as React from 'react';

import {
  Box,
  Button,
  Typography,
  InputAdornment,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableCell,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddIcon from '@mui/icons-material/Add';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { CTextField } from '../../components/common/TextField';
import { CActionButton, CPageButton, CPageArrowButton } from '../../components/common/Button';
import DimensionDefaultModal from '../../modals/admin/DimensionDefaultModal';

import { 
  getDimensionDefaults, 
  getDimensionDefaultsByCube,
  deleteDimensionDefault 
} from '../../slices/dimensionDefaults';
import cubeService from '../../services/cube';

export default function DimensionDefaults() {
  const [openModal, setOpenModal] = React.useState(false);
  const [mode, setMode] = React.useState('Create');
  const [selectedDefault, setSelectedDefault] = React.useState(null);
  const [cubes, setCubes] = React.useState([]);
  const [selectedCubeId, setSelectedCubeId] = React.useState('all');
  const [searchTerm, setSearchTerm] = React.useState('');
  
  const dispatch = useDispatch();
  const dimensionDefaults = useSelector(state => 
    selectedCubeId === 'all' 
      ? state.dimensionDefaults?.dimensionDefaults || []
      : state.dimensionDefaults?.cubeDefaults || []
  );
  const isLoading = useSelector(state => state.dimensionDefaults?.isLoading);

  // Load cubes on mount
  React.useEffect(() => {
    cubeService.getCubes(1, 100)
      .then(res => {
        setCubes(res.items || []);
      })
      .catch(err => console.error('Error loading cubes:', err));
  }, []);

  // Load dimension defaults based on selected cube
  React.useEffect(() => {
    if (selectedCubeId === 'all') {
      dispatch(getDimensionDefaults({ page: 1, pageSize: 100 }));
    } else if (selectedCubeId) {
      dispatch(getDimensionDefaultsByCube(selectedCubeId));
    }
  }, [dispatch, selectedCubeId]);

  const handleOpenModal = () => {
    setMode('Create');
    setSelectedDefault(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDefault(null);
  };

  const handleEdit = (dimDefault) => {
    setMode('Edit');
    setSelectedDefault(dimDefault);
    setOpenModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this dimension default?')) {
      dispatch(deleteDimensionDefault(id))
        .then(() => {
          // Refresh the list
          if (selectedCubeId === 'all') {
            dispatch(getDimensionDefaults({ page: 1, pageSize: 100 }));
          } else {
            dispatch(getDimensionDefaultsByCube(selectedCubeId));
          }
        })
        .catch(err => {
          alert('Error deleting dimension default: ' + (err.message || 'Unknown error'));
        });
    }
  };

  const handleRefresh = () => {
    if (selectedCubeId === 'all') {
      dispatch(getDimensionDefaults({ page: 1, pageSize: 100 }));
    } else {
      dispatch(getDimensionDefaultsByCube(selectedCubeId));
    }
  };

  const handleSuccess = () => {
    // Refresh the list after successful save
    handleRefresh();
  };

  // Filter defaults based on search term
  const filteredDefaults = React.useMemo(() => {
    if (!searchTerm) return dimensionDefaults;
    
    const lowerSearch = searchTerm.toLowerCase();
    return dimensionDefaults.filter(dd => 
      (dd.dimension || '').toLowerCase().includes(lowerSearch) ||
      (dd.default_member || '').toLowerCase().includes(lowerSearch) ||
      (dd.description || '').toLowerCase().includes(lowerSearch)
    );
  }, [dimensionDefaults, searchTerm]);

  const getCubeName = (dimDefault) => {
    // Use cube_name from API response if available (preferred)
    if (dimDefault?.cube_name) {
      return dimDefault.cube_name;
    }
    // Fallback to lookup from cubes array (for backward compatibility)
    const cube = cubes.find(c => c.id === dimDefault?.cube_id);
    return cube ? cube.cube_name : `Cube ${dimDefault?.cube_id || 'Unknown'}`;
  };

  return (
    <Box className='w-full flex flex-column flex-basis flex-gap'>
      <Box sx={{paddingTop: 2, paddingLeft: 2}}>
        <Typography 
          sx={{ 
            fontSize: 24, 
            fontWeight: 600, 
            color: '#4A85F6',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          Dimension Defaults
          <Tooltip title="Configure default member values for dimensions in each cube. These defaults are used when a dimension is not explicitly specified in a query.">
            <InfoOutlinedIcon fontSize="small" sx={{ color: '#666' }} />
          </Tooltip>
        </Typography>
      </Box>

      <Box sx={{paddingTop: 2, gap: 2}} className='flex flex-row align-center'>
        <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
          <InputLabel>Filter by Cube</InputLabel>
          <Select
            value={selectedCubeId}
            label="Filter by Cube"
            onChange={(e) => setSelectedCubeId(e.target.value)}
          >
            <MenuItem value="all">All Cubes</MenuItem>
            {cubes.map(cube => (
              <MenuItem key={cube.id} value={cube.id}>
                {cube.cube_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <CTextField
          label=""
          id="search-text-field"
          placeholder='Search dimensions or defaults...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, background: 'white'}}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchOutlinedIcon /></InputAdornment>,
          }}
        />

        <Tooltip title="Refresh">
          <IconButton 
            onClick={handleRefresh}
            sx={{ 
              bgcolor: 'white',
              border: '1px solid #e0e0e0',
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Button
          sx={{
            textTransform: 'none',
            height: 54,
            borderRadius: 2
          }}
          endIcon={<AddIcon />}
          variant='contained'
          onClick={handleOpenModal}
        >
          Add Default
        </Button>
      </Box>

      <Box className='flex-basis'>
        <Paper className='h-full flex flex-column' sx={{borderRadius: 4, margin: 1}}>
          <TableContainer className='flex-basis'>
            <Table sx={{ minWidth: 650, height: '100%' }} size="small" aria-label="a dense table">
              <TableHead sx={{ height: 64}}>
                <TableRow>
                  <TableCell>Cube</TableCell>
                  <TableCell>Dimension</TableCell>
                  <TableCell>Default Member</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell sx={{width: 120}}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 10 }).map((_, i) => {
                  const dimDefault = filteredDefaults[i];
                  return (
                    <TableRow
                      key={dimDefault ? dimDefault.id : 1000000+i}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 }, height: 50 }}
                    >
                      {dimDefault ? (
                        <>
                          <TableCell component="th" scope="row">
                            <Chip 
                              label={getCubeName(dimDefault)} 
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {dimDefault.dimension}
                              </Typography>
                              {dimDefault.dimension_path && (
                                <Typography variant="caption" color="textSecondary">
                                  {dimDefault.dimension_path}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={dimDefault.default_member || '(not set)'} 
                              size="small"
                              color={dimDefault.default_member ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={dimDefault.description || 'No description'}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  maxWidth: 300,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {dimDefault.description || <em style={{ color: '#999' }}>No description</em>}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <CActionButton
                              startIcon={<BorderColorIcon />}
                              onClick={() => handleEdit(dimDefault)}
                            />
                            <CActionButton
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => handleDelete(dimDefault.id)}
                            />
                          </TableCell>
                        </>
                      ) : (
                        // Render empty cells for blank rows
                        <>
                          <TableCell component="th" scope="row" sx={{borderBottom: 'none'}}></TableCell>
                          <TableCell sx={{borderBottom: 'none'}}></TableCell>
                          <TableCell sx={{borderBottom: 'none'}}></TableCell>
                          <TableCell sx={{borderBottom: 'none'}}></TableCell>
                          <TableCell sx={{borderBottom: 'none'}}></TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              height: 80,
              paddingLeft: 8,
              paddingRight: 8,
              borderTop: '1px solid rgba(224, 224, 224, 1)'
            }}
            className='flex flex-row align-center justify-between'
          >
            <CPageArrowButton startIcon={<ArrowBackIcon/>} >Previous</CPageArrowButton>
            <Box sx={{color: '#f5f5f5'}}>
              <CPageButton sx={{border: '1px solid #f1f1f1'}}>1</CPageButton>
              <CPageButton>2</CPageButton>
              <CPageButton>3</CPageButton>
              <CPageButton>...</CPageButton>
              <CPageButton>8</CPageButton>
              <CPageButton>9</CPageButton>
              <CPageButton>10</CPageButton>
            </Box>
            <CPageArrowButton endIcon={<ArrowForwardIcon/>} >Next</CPageArrowButton>
          </Box>
        </Paper>
      </Box>

      <DimensionDefaultModal
        open={openModal}
        mode={mode}
        data={selectedDefault}
        handleClose={handleCloseModal}
        onSuccess={handleSuccess}
      />
    </Box>
  );
}

